/** @format */

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  AnalyticsService,
  CartService,
  CookieService,
  HttpService,
  Store,
  StoreService,
  VirtualHostCookieService,
  WINDOW,
} from '@shared/data-access/services';
import {
  Component,
  DOCUMENT,
  OnInit,
  PLATFORM_ID,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, filter, map, mergeMap, take, tap } from 'rxjs';

import { AuthenticationService } from '@shared/authentication/data-access';
import { Customer } from '@shared/data-access/interfaces';
import { SeoService } from '@seo/data-access';
import { chain } from '@core/chain';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'home-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthenticationService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly cartService = inject(CartService);
  private readonly storeService = inject(StoreService);
  private readonly cookieService = inject(CookieService);
  private readonly httpService = inject(HttpService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SeoService);
  private readonly virtualHostCookieService = inject(VirtualHostCookieService);

  /**
   * @description
   * The chain identifier abbreviation
   */
  public chainIdentifierAbbr = chain.chainIdentifierAbbr;

  /**
   * @description
   * The Customer object from application state
   */
  private customer$ = this.store.select('CUSTOMER');

  constructor() {
    // Check authentication status and set up authentication status stream
    this.authService.localAuthSetup();
  }

  ngOnInit(): void {
    // Reset the analytics
    if (!this.window.siteData) {
      this.analyticsService.resetAnalytics();
    }

    this.observeNavigation().subscribe();

    if (isPlatformBrowser(this.platformId)) {
      if (navigator.userAgent.indexOf('Mac OS X') !== -1) {
        this.document.body.classList.add('mac');
      }

      // Virtual Host chain identifier
      this.virtualHostCookieService.setVirtualHostCookie();

      /*
       * Get the promoId query parameter from the URL
       */
      const promoId = this.route.snapshot.queryParams['param'];
      if (promoId && !this.cookieService.check('promoId')) {
        this.cookieService.set(
          'promoId',
          this.httpService.getUrlParameter('param'),
          undefined,
          '/',
        );
      }

      // Initialize the Cart Service
      this.cartService.initialize();

      // Check cookie and populate Store
      this.authService.checkIfReturningCustomer();

      // Set default store
      this.storeService.initialize();
    }
  }

  /* istanbul ignore next */
  public observeNavigation(): Observable<any> {
    return this.router.events.pipe(
      filter((event) => {
        return event instanceof NavigationEnd;
      }),
      map(() => {
        return this.route;
      }),
      map((route: any) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap((route: ActivatedRoute) => {
        return new Observable((observer) => {
          observer.next({
            data: route.data,
            params: route.params,
            queryParams: route.queryParams,
          });
          observer.complete();
        });
      }),
      tap((route: any) => {
        /*
         * Only execute on browser
         */
        const seoName: string | null = route.params.value.seoName
          ? route.params.value.seoName.replaceAll(/[^a-zA-Z0-9-]/g, '')
          : null;

        this.seoService
          .initialize(seoName || 'homepage', this.router.url.split('?')[0])
          .pipe(take(1))
          .subscribe();

        if (isPlatformBrowser(this.platformId)) {
          return this.initializeAnalyticsService(route.params);
        }
      }),
    );
  }

  public async initializeAnalyticsService(params) {
    // Get the current query parameters from the route query params
    const CURRENT_QUERY_PARAMS = params;
    // Get the current Date/Time
    const CURRENT_DATETIME = new Date();

    // Set the chain
    this.analyticsService.setChain(chain.chainName);

    // Set the Error ID (if present)
    this.analyticsService.setErrorId();

    // Set the Error Message (if present)
    this.analyticsService.setErrorMessage();

    // Set the referring URL
    this.analyticsService.setReferringPageUrl(this.document.referrer);

    // Set the Page Type
    this.analyticsService.setType('page', 'Home Page');

    // Set the server date
    this.analyticsService.setServerDate(this.analyticsService.getFormattedDate(CURRENT_DATETIME));

    // Set the server time
    this.analyticsService.setServerTime(this.analyticsService.getFormattedTime(CURRENT_DATETIME));

    // Get the Customer from the Store and populate related analytics
    this.customer$.subscribe((customer: Customer) => {
      // Set the Loyalty ID
      this.analyticsService.setLoyaltyID(customer.x_acNumber || '');

      // Set the LoyaltyBalance
      this.analyticsService.setLoyaltyPointsBalance(customer.x_acBalancePoints || '');

      // Set the session ID
      this.analyticsService.setSessionID(customer.sessionId);

      // Set the user ID
      this.analyticsService.setUserID(customer.userId);

      // Set the visits count
      this.analyticsService.setVisitNumber(customer.visitCount);
    });

    // Set the base Search properties
    this.analyticsService.setSearchPageBaseProperties({
      searchDetails: {
        DYMTerm: CURRENT_QUERY_PARAMS.dymterm || CURRENT_QUERY_PARAMS.DYMTerm || '',
        DYMType: CURRENT_QUERY_PARAMS.didyoumean || CURRENT_QUERY_PARAMS.didYouMean || '',
        replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm || '',
        searchTerm: CURRENT_QUERY_PARAMS.searchTerm || '',
        searchType: CURRENT_QUERY_PARAMS.searchType || '',
      },
    });

    // Validate authentication status
    const isAuthenticated: boolean = await this.authService
      .getIsAuthenticated$()
      .toPromise()
      .catch((err) => {
        this.analyticsService.setErrorId('DCSG_NGX_HOME_AUTHENTICATED_ERROR');
        this.analyticsService.setErrorMessage(`HOME:${err.message}`);

        // Return false because there was an error validating authentication status
        return false;
      });

    // Set the logged in flag
    this.analyticsService.setLoggedIn(`${isAuthenticated}`);

    // Set the LoggedInOccured property
    this.analyticsService.setLoggedInOccurred(`${isAuthenticated}`);
  }
}
